import { derived, get, writable } from 'svelte/store';

import { silentKinds } from '$lib/api-error-handling';
import { client as apiClient } from '$lib/api/client.gen';
import {
	browserDevLogin as devLogin,
	browserGoogleLogin as googleLogin,
	browserLogout as logout,
} from '$lib/api/sdk.gen';
import { isLocal } from '$lib/build-info';

import type { User } from '../api/types.gen';
import { signInFailure } from './auth-errors';
import { safeLocalStorageGet, safeLocalStorageSet } from './local-storage';
import { store as usersStore } from './users';

export type { User };

const STORAGE_KEY = 'ds:user';
export { SignInError } from './auth-errors';

const initial = typeof window !== 'undefined' ? safeLocalStorageGet<User>(STORAGE_KEY) : null;

export const user = writable<User | null>(initial);
const loginRedirect = writable<string | null>(null);

// Persist changes to localStorage in the browser
user.subscribe((u) => {
	safeLocalStorageSet(STORAGE_KEY, u ?? null);
});

// Session changes in another tab must take effect here too. In particular,
// stores which gate writes on the active user must never continue a queued
// request under credentials that were cleared or switched elsewhere.
if (typeof window !== 'undefined') {
	window.addEventListener('storage', (event) => {
		if (event.key !== STORAGE_KEY || event.storageArea !== window.localStorage) return;
		try {
			user.set(event.newValue === null ? null : (JSON.parse(event.newValue) as User));
		} catch {
			user.set(null);
		}
	});
}

export const isSignedIn = derived(user, ($user) => $user !== null);

export function sanitizeRedirect(
	path: string | null | undefined,
	origin = window.location.origin,
): string | null {
	if (!path) return null;
	if (!path.startsWith('/')) return null;

	try {
		const url = new URL(path, origin);
		if (url.origin !== origin) return null;
		return url.pathname + url.search + url.hash;
	} catch {
		return null;
	}
}

export function consumeLoginUrl(): string {
	const redirectTo = get(loginRedirect);
	loginRedirect.set(null);
	if (!redirectTo) return '/';

	const searchParams = new URLSearchParams({ redirect: redirectTo });
	return `/?${searchParams.toString()}`;
}

export async function signInWithDevEmail(email: string) {
	if (!isLocal) {
		throw new Error('Dev email sign-in is not available in this environment');
	}

	let result;
	try {
		result = await devLogin({
			body: { email },
			meta: { errors: silentKinds('SIGNUP_CLOSED_BETA') },
		});
	} catch (error) {
		throw signInFailure(error);
	}
	const { response, data, error } = result;
	if (!response?.ok || !data) {
		throw signInFailure(error);
	}
	user.set(data.user);
}

export async function signInWithGoogle(idToken: string) {
	let result;
	try {
		result = await googleLogin({
			body: { id_token: idToken },
			meta: { errors: silentKinds('SIGNUP_CLOSED_BETA') },
		});
	} catch (error) {
		throw signInFailure(error);
	}
	const { response, data, error } = result;
	if (!response?.ok || !data) {
		throw signInFailure(error);
	}
	user.set(data.user);
}

export async function signOut({
	redirectTo,
	wait = true,
	throwOnError = false,
}: {
	redirectTo?: string | null;
	wait?: boolean;
	throwOnError?: boolean;
} = {}) {
	try {
		if (wait) {
			await logout({ throwOnError: throwOnError });
		} else {
			logout();
		}
	} finally {
		loginRedirect.set(sanitizeRedirect(redirectTo));
		user.set(null);
		// Imported lazily to keep the voice editor out of the auth bundle; the
		// cache may hold another session's audio even if this page never used it.
		void import('$lib/editor/voice-studio/preview-cache')
			.then(({ voicePreviewCache }) => voicePreviewCache.clear())
			.catch(() => {});
	}
}

export async function refreshUser() {
	const currentUser = get(user);
	if (!currentUser) return;

	try {
		// Not `getUserRedirect()`: that's an extra 307 hop, and this also seeds the users store.
		// Copied without store metadata: the cached object can change in place, and this one persists.
		const { cacheTs, wasCacheInvalidated, avatarTs, ...fresh } = await usersStore.loadUser(
			currentUser.id,
			{ private: true, force: true },
		);
		user.set(fresh);
	} catch (error) {
		console.error('Failed to refresh user:', error);
		// TODO: If refresh fails, perhaps sign out or leave as is
	}
}

export function useCredentialsIncludeRequestInterceptor() {
	// Enable cookies for all API requests
	const _ = apiClient.interceptors.request.use((r) => {
		return new Request(r, { credentials: 'include' });
	});
}

export default {
	user,
	isSignedIn,
	consumeLoginUrl,
	signInWithDevEmail,
	signInWithGoogle,
	signOut,
	useCredentialsIncludeRequestInterceptor,
};
